import type { PaletteMode } from '@mui/material'
import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react'

const STORAGE_KEY = 'co2ai-theme-mode'

interface ThemeContextValue {
	mode: PaletteMode
	toggleMode: () => void
}

const ThemeContext = createContext<ThemeContextValue>({
	mode: 'light',
	toggleMode: () => undefined,
})

const ThemeContextProvider = ({ children }: { children: React.ReactNode }) => {
	const [mode, setMode] = useState<PaletteMode>(() => {
		const stored = localStorage.getItem(STORAGE_KEY)
		return stored === 'dark' ? 'dark' : 'light'
	})

	const toggleMode = useCallback(() => {
		setMode(prev => {
			const next = prev === 'light' ? 'dark' : 'light'
			localStorage.setItem(STORAGE_KEY, next)
			return next
		})
	}, [])

	const value = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode])

	return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

const useThemeMode = () => useContext(ThemeContext)

export { ThemeContextProvider, useThemeMode }
